import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Label, Nav, NavItem, NavLink } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";

import AppModal from "../../Common/AppModal";
import { getCustomersListData } from "../../../store/customers/action";
import { linkEmailToContactPersonApiCall } from "../../../helpers/fakebackend_helper";

/**
 * Link a loose email address to a contact (Task 24737).
 *
 * An address typed straight into the compose modal's To/Cc/Bcc box becomes a
 * chip with no customer on it. The email then goes out as a raw string,
 * attributed to nobody, and never shows against a customer. Until now the only
 * cure was to abandon the half-written email, go and build the contact by hand,
 * and start again.
 *
 * This is the modal behind the warning badge on such a chip. It offers the two
 * answers the task asked for:
 *
 *   EXISTING - add this address to a contact that already exists.
 *   NEW      - create a contact and link it to one or more customers.
 *
 * On success it calls `onLinked` with an address-book-shaped row, so the caller
 * can rebuild the chip exactly as if the contact had been picked from the
 * dropdown in the first place. That is the point: after linking, the address
 * travels down the same path as any other contact, not a second one.
 *
 * Props:
 *   open, onClose      - modal control
 *   emailAddress       - the address being linked (read-only here)
 *   suggestedName      - the name the chip guessed from the address, used to
 *                        pre-fill first/last name on the NEW tab
 *   contactOptions     - the compose modal's own address book, one entry per
 *                        (customer, contact, email). Used for the EXISTING
 *                        picker so no extra fetch is needed.
 *   onLinked(row)      - called with the new address-book row
 */
const TAB_EXISTING = "existing";
const TAB_NEW = "new";

export default function LinkEmailToContact({
  open,
  onClose,
  emailAddress,
  suggestedName,
  contactOptions,
  onLinked,
}) {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(TAB_EXISTING);
  const [saving, setSaving] = useState(false);

  // EXISTING
  const [selectedContact, setSelectedContact] = useState(null);

  // NEW - pre-filled from whatever the chip guessed out of the address
  // ("mark.jones@..." -> "Mark Jones"), because retyping what is already on
  // screen is the kind of friction that stops people doing this at all.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // Change #100255 - their job title at the customer(s) linked below.
  const [jobTitle, setJobTitle] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const customersList = useSelector(
    (state) => state.Customers?.customersDataList?.customer_list
  );

  useEffect(() => {
    if (!open) return;
    dispatch(getCustomersListData());

    const parts = String(suggestedName || "").trim().split(/\s+/);
    setFirstName(parts[0] || "");
    setLastName(parts.slice(1).join(" ") || "");
    setJobTitle("");
    setSelectedContact(null);
    setSelectedCustomers([]);
    setActiveTab(TAB_EXISTING);
  }, [open, suggestedName, dispatch]);

  // One option per (customer, contact) pair, which is what the backend needs:
  // an email address row hangs off a relationship, not off a contact alone, so
  // a contact linked to three customers is three separate choices here. The
  // label says which customer so the choice is not a guess.
  //
  // De-duplicated by relationship, because the address book carries one entry
  // per EMAIL and a contact with three addresses would otherwise appear three
  // times as the same choice.
  const existingOptions = useMemo(() => {
    const seen = new Set();
    return (contactOptions || [])
      .map((option) => option?.item || option)
      .filter((item) => {
        const id = item?.CustomerToContactPersonRelationship_Id;
        if (!id || seen.has(String(id))) return false;
        seen.add(String(id));
        return true;
      })
      .map((item) => ({
        value: item.CustomerToContactPersonRelationship_Id,
        label: `${item.ContactPerson_FullName || "(no name)"}${
          item.Customer_FullName ? ` - ${item.Customer_FullName}` : ""
        }`,
      }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [contactOptions]);

  const customerOptions = useMemo(
    () =>
      (customersList || [])
        .map((customer) => ({
          value: customer.Id,
          label: customer.FullName || customer.BusinessName || `#${customer.Id}`,
        }))
        .sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    [customersList]
  );

  const canSave =
    activeTab === TAB_EXISTING
      ? !!selectedContact
      : (!!firstName.trim() || !!lastName.trim()) &&
        selectedCustomers.length > 0;

  const handleSave = async () => {
    if (!canSave || saving) return;

    const payload =
      activeTab === TAB_EXISTING
        ? {
            EmailAddress: emailAddress,
            CustomerToContactPersonRelationship_Id: selectedContact.value,
          }
        : {
            EmailAddress: emailAddress,
            FirstName: firstName.trim(),
            LastName: lastName.trim(),
            JobTitle: jobTitle.trim() || null,
            Customer_Id: selectedCustomers.map((c) => c.value),
          };

    setSaving(true);
    try {
      const response = await linkEmailToContactPersonApiCall([payload]);

      if (response?.status === 200) {
        const rows = response?.contactpersonemailaddress_list || [];
        if (!rows.length) {
          // The write succeeded but nothing came back to rebuild the chip
          // with, so say so rather than silently leaving the chip unlinked and
          // letting the user believe it is fixed.
          toast.warning(
            "Saved, but the contact could not be read back - reopen the email to pick it from the list."
          );
          onClose();
          return;
        }
        toast.success(response.message);
        onLinked(rows);
        onClose();
      } else {
        toast.error(response?.message || "Could not link the email address");
      }
    } catch (e) {
      toast.error("Could not link the email address");
    }
    setSaving(false);
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Link this email to a contact"
      icon="ri-user-add-line"
      onSubmit={handleSave}
      submitLabel={activeTab === TAB_EXISTING ? "Add to contact" : "Create and link"}
      submitDisabled={!canSave}
      loading={saving}
      dialogProps={{ maxWidth: "sm", fullWidth: true }}
    >
      <p className="text-muted mb-3">
        <i className="ri-mail-line me-1"></i>
        <strong>{emailAddress}</strong> was typed in by hand, so it is not
        linked to a customer. Link it and the email is recorded against them -
        and the address is there next time.
      </p>

      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            role="button"
            className={activeTab === TAB_EXISTING ? "active" : ""}
            onClick={() => setActiveTab(TAB_EXISTING)}
          >
            Existing contact
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            role="button"
            className={activeTab === TAB_NEW ? "active" : ""}
            onClick={() => setActiveTab(TAB_NEW)}
          >
            New contact
          </NavLink>
        </NavItem>
      </Nav>

      {activeTab === TAB_EXISTING ? (
        <div>
          <Label>Contact</Label>
          <Select
            options={existingOptions}
            value={selectedContact}
            onChange={setSelectedContact}
            placeholder="Search contacts..."
            classNamePrefix="select"
            noOptionsMessage={() => "No contacts on this email's address book"}
          />
          <small className="text-muted d-block mt-2">
            Contacts are listed per customer, because an email address belongs
            to a contact <em>at</em> a customer. If the contact is not here, use
            the New contact tab.
          </small>
        </div>
      ) : (
        <div>
          <div className="d-flex gap-2 mb-3">
            <div className="w-50">
              <Label>First name</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="w-50">
              <Label>Last name</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
          </div>

          {/* Change #100255 - "can we get job title on here". */}
          <div className="mb-3">
            <Label>Job title</Label>
            <Input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Claims Handler (optional)"
            />
          </div>

          <Label>
            Customers <span className="text-danger">*</span>
          </Label>
          <Select
            isMulti
            options={customerOptions}
            value={selectedCustomers}
            onChange={(value) => setSelectedCustomers(value || [])}
            placeholder="Link this contact to one or more customers..."
            classNamePrefix="select"
          />
          <small className="text-muted d-block mt-2">
            At least one. The customer link is the whole point - without it the
            new contact would be just as unattributed as the typed address.
          </small>
        </div>
      )}
    </AppModal>
  );
}
